import { type ReactNode, useEffect, useRef, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAdminStatus } from '../hooks/useAdminStatus';

interface AppLayoutProps {
  children: ReactNode;
  /** Set `true` for full-height pages (chat, hybrid) so <main> fills remaining
   *  viewport height below the header instead of using normal padding. */
  fullHeight?: boolean;
}

function MountainLogo() {
  return (
    <div className="relative w-9 h-9 bg-gradient-to-br from-spotify-green to-spotify-green-dark rounded-xl flex items-center justify-center shadow-lg flex-shrink-0">
      <svg className="w-5 h-5 text-black" fill="currentColor" viewBox="0 0 24 24">
        <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z" />
      </svg>
    </div>
  );
}

function navLinkClass({ isActive }: { isActive: boolean }): string {
  return [
    'px-3 py-1.5 text-sm rounded-lg transition-colors',
    isActive
      ? 'bg-spotify-green/15 text-spotify-green font-medium'
      : 'text-spotify-gray-400 hover:text-white hover:bg-white/5',
  ].join(' ');
}

function RegisterDropdown() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  const items: { to: string; label: string; desc: string }[] = [
    { to: '/hybrid', label: 'Hybrid', desc: 'Chat + interactive cards' },
    { to: '/chat', label: 'Chat', desc: 'Conversational flow' },
    { to: '/register-song', label: 'Form', desc: 'Step-by-step form' },
  ];

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        className="flex items-center gap-1 px-3 py-1.5 text-sm text-spotify-gray-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
      >
        Register
        <svg
          className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-1 w-60 bg-spotify-gray-900 border border-white/10 rounded-xl shadow-xl py-1 overflow-hidden"
        >
          {items.map((it) => (
            <Link
              key={it.to}
              to={it.to}
              role="menuitem"
              onClick={() => setOpen(false)}
              className="block px-4 py-2 hover:bg-white/5 transition-colors"
            >
              <p className="text-white text-sm font-medium">{it.label}</p>
              <p className="text-spotify-gray-400 text-xs">{it.desc}</p>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function UserMenu() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  if (!user) return null;

  const username = user.email?.split('@')[0] ?? 'User';

  return (
    <div className="flex items-center gap-2">
      <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-spotify-gray-800/60 border border-white/10">
        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-spotify-green to-spotify-green-dark flex items-center justify-center">
          <span className="text-xs font-bold text-black uppercase">
            {username.charAt(0)}
          </span>
        </div>
        <span className="text-sm text-spotify-gray-300 max-w-[120px] truncate">
          {username}
        </span>
      </div>
      <button
        onClick={async () => {
          await signOut();
          navigate('/login');
        }}
        aria-label="Sign out"
        title="Sign out"
        className="p-2 rounded-full bg-spotify-gray-800/60 border border-white/10 hover:bg-spotify-gray-700/60 transition-colors group"
      >
        <svg className="w-4 h-4 text-spotify-gray-400 group-hover:text-white transition-colors"
             fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
        </svg>
      </button>
    </div>
  );
}

export default function AppLayout({ children, fullHeight = false }: AppLayoutProps) {
  const adminStatus = useAdminStatus();

  return (
    <div className={fullHeight ? 'flex flex-col h-screen bg-spotify-black' : 'min-h-screen bg-spotify-black'}>
      <header className="sticky top-0 z-40 border-b border-white/5 bg-spotify-gray-900/80 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <Link to="/dashboard" className="flex items-center gap-2 hover:opacity-80 transition-opacity min-w-0">
            <MountainLogo />
            <div className="hidden sm:block min-w-0">
              <h1 className="text-white font-display font-bold text-base tracking-tight truncate">
                Sherpa
              </h1>
              <p className="text-[10px] text-spotify-gray-500 truncate">Tenzing</p>
            </div>
          </Link>

          <nav className="flex items-center gap-1">
            <NavLink to="/dashboard" className={navLinkClass}>
              Dashboard
            </NavLink>
            <RegisterDropdown />
            <NavLink to="/writers" className={navLinkClass}>
              Writers
            </NavLink>
            {adminStatus === 'authorized' && (
              <NavLink to="/admin" className={navLinkClass}>
                Admin
              </NavLink>
            )}
          </nav>

          <UserMenu />
        </div>
      </header>

      <main className={fullHeight ? 'flex-1 min-h-0 flex flex-col' : ''}>
        {children}
      </main>
    </div>
  );
}
